import { Layout } from '../components/Layout';
import { Masthead } from '../components/Masthead';
import { Toc } from '../components/Toc';
import { Section, SubHead } from '../components/Section';
import { Definition, Note, Card, CardGrid } from '../components/Blocks';
import { Figure } from '../components/Figure';
import { Exercise, Solution } from '../components/Exercise';

const toc = [
  { id: 'ph-camera',  label: 'Meet your camera' },
  { id: 'ph-setup',   label: 'First-time setup — five things' },
  { id: 'ph-exposure',label: 'The three things that change brightness' },
  { id: 'ph-modes',   label: 'Pick a mode (the simple version)' },
  { id: 'ph-focus',   label: 'How to focus' },
  { id: 'ph-recipes', label: 'Settings for five common photos' },
  { id: 'ph-habits',  label: 'Five small habits that improve every photo' },
  { id: 'ph-try',     label: 'Try these this week' },
];

export function Photography() {
  return (
    <Layout accent="ph" title="Photography · EOS 6D Mk II">
      <Masthead
        volume="Vol. XI · Compiled 2026"
        kicker="A beginner's guide"
        title="Photography"
        titleEm="· Canon EOS 6D Mark II"
        subline="A friendly start with your camera. The fewest words to get you taking photos you like — no jargon, no quizzes, no homework."
        byline="By Marouane BENALLA · For new owners of the 6D Mk II · Open & free"
      />

      <Toc items={toc} />

      <Section id="ph-camera" num="01" title="Meet your camera">
        <p>
          The Canon EOS 6D Mark II is a <strong>full-frame DSLR</strong>. Two pieces of jargon, both simple:
        </p>
        <ul className="list-disc pl-6 my-4 space-y-2">
          <li><em>Full-frame</em> means the sensor is the same size as a 35 mm film negative. Bigger sensor → better in low light, easier to get a blurry background.</li>
          <li><em>DSLR</em> means it has a mirror inside that flips up when you take the photo. You see the scene through the optical viewfinder; you can also flip out the back screen and use Live View.</li>
        </ul>

        <p>Two things make this body friendly for a beginner:</p>
        <ul className="list-disc pl-6 my-4 space-y-1">
          <li>The <strong>flip-out touchscreen</strong> on the back. Tap to focus, drag to move the focus point.</li>
          <li>Built-in <strong>GPS &amp; Wi-Fi</strong>. Photos are tagged with where you took them; you can copy them to your phone over Wi-Fi without a cable.</li>
        </ul>

        <SubHead>Top of the camera, in plain words</SubHead>
        <Figure label="Fig. 1" caption="The four controls you use most: the mode dial (left), the small top screen (current settings), the main wheel under your index finger, and the big silver button — the shutter.">
          <svg viewBox="0 0 820 240" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M 50 110 Q 60 40 150 40 L 360 40 Q 380 40 400 80 Q 420 40 440 40 L 720 40 Q 770 40 770 110 L 770 170 Q 770 200 740 200 L 100 200 Q 50 200 50 160 Z"
              fill="#efe8d8" stroke="#1a1a1f" strokeWidth="1.6"
            />
            <rect x="370" y="30" width="60" height="16" fill="#6d4c26" stroke="#1a1a1f" />
            <text x="400" y="24" textAnchor="middle" fontSize="10" fill="#6d4c26">flash slot</text>
            <circle cx="150" cy="110" r="44" fill="#6d4c26" stroke="#1a1a1f" strokeWidth="1.2" />
            <circle cx="150" cy="110" r="36" fill="#efe8d8" stroke="#1a1a1f" />
            <text x="150" y="107" textAnchor="middle" fontSize="11" fontWeight="700">Mode dial</text>
            <text x="150" y="122" textAnchor="middle" fontSize="9">turn to pick mode</text>
            <rect x="240" y="90" width="130" height="60" fill="#111" stroke="#1a1a1f" />
            <text x="305" y="120" textAnchor="middle" fontSize="11" fill="#d2a373">1/125 · f/5.6</text>
            <text x="305" y="138" textAnchor="middle" fontSize="10" fill="#d2a373">ISO 400</text>
            <text x="305" y="172" textAnchor="middle" fontSize="9" fill="#6a6a74">small screen — current settings</text>
            <circle cx="700" cy="80" r="22" fill="#6d4c26" stroke="#1a1a1f" />
            <text x="700" y="65" textAnchor="middle" fontSize="9">main wheel</text>
            <text x="700" y="82" textAnchor="middle" fontSize="9" fill="#efe8d8">↻</text>
            <circle cx="655" cy="100" r="14" fill="#a66a3d" stroke="#1a1a1f" strokeWidth="1.2" />
            <text x="655" y="132" textAnchor="middle" fontSize="9">shutter button</text>
          </svg>
        </Figure>
      </Section>

      <Section id="ph-setup" num="02" title="First-time setup — five things">
        <p>Do these once, then forget about them.</p>
        <ol className="list-decimal pl-6 my-4 space-y-2">
          <li><strong>Charge the battery.</strong> Use the supplied charger; first charge takes 1–2 hours.</li>
          <li><strong>Format the SD card in the camera.</strong> <code>Menu → wrench icon → Format card</code>. Safer than formatting on a computer.</li>
          <li><strong>Set date and time.</strong> Used in the file metadata, helps you find your photos later.</li>
          <li><strong>Image quality: Large JPEG to start.</strong> Smaller files, ready-to-share. (Switch to RAW later when you want to edit colour and exposure in Lightroom.)</li>
          <li><strong>Turn off the beep.</strong> Your future self at concerts and ceremonies will thank you.</li>
        </ol>
        <p>You're ready. Battery in, lens on, card in, dial set to <strong>Av</strong>.</p>
      </Section>

      <Section id="ph-exposure" num="03" title="The three things that change brightness">
        <p>
          A photo's brightness comes from three settings. Each one also has a <em>side effect</em> — and the side effect is usually what you actually choose between.
        </p>

        <Figure label="Fig. 2" caption="Three knobs, one brightness. You change one — change another to compensate, or let the camera handle it.">
          <svg viewBox="0 0 820 240" xmlns="http://www.w3.org/2000/svg">
            <polygon points="410,30 150,210 670,210" fill="none" stroke="#6d4c26" strokeWidth="1.6"/>
            <circle cx="410" cy="30"  r="10" fill="#6d4c26"/>
            <circle cx="150" cy="210" r="10" fill="#6d4c26"/>
            <circle cx="670" cy="210" r="10" fill="#6d4c26"/>
            <text x="410" y="18" textAnchor="middle" fontSize="13" fontWeight="700" fill="#1a1a1f">Aperture</text>
            <text x="410" y="68" textAnchor="middle" fontSize="10" fill="#6a6a74">side effect: background blur</text>
            <text x="150" y="232" textAnchor="middle" fontSize="13" fontWeight="700" fill="#1a1a1f">Shutter speed</text>
            <text x="150" y="190" textAnchor="middle" fontSize="10" fill="#6a6a74">side effect: motion blur</text>
            <text x="670" y="232" textAnchor="middle" fontSize="13" fontWeight="700" fill="#1a1a1f">ISO</text>
            <text x="670" y="190" textAnchor="middle" fontSize="10" fill="#6a6a74">side effect: noise / grain</text>
          </svg>
        </Figure>

        <SubHead>Aperture — how blurry the background is</SubHead>
        <p>
          Measured by <strong>f-numbers</strong> like <code>f/2.8</code>, <code>f/5.6</code>, <code>f/8</code>.
          <strong> Smaller number = wider opening = blurrier background</strong>. Bigger number = sharper from front to back.
        </p>

        <SubHead>Shutter speed — does motion freeze or blur?</SubHead>
        <p>Measured in seconds or fractions of a second. <strong>Faster = freezes motion</strong>. Slower = lets motion blur (and lets in more light).</p>

        <SubHead>ISO — how sensitive the sensor is</SubHead>
        <p><strong>Lower = cleaner photo</strong>. Higher = grainier, but lets you shoot in less light.</p>

        <Note label="Beginner shortcut">
          Set ISO to <strong>Auto</strong> for now. <code>ISO button → wheel → A</code>. The camera will pick a reasonable ISO. Worry about it manually only when you start shooting RAW and editing.
        </Note>
      </Section>

      <Section id="ph-modes" num="04" title="Pick a mode (the simple version)">
        <p>Letters on the top dial. Translated:</p>
        <table className="w-full my-5 border-collapse">
          <thead>
            <tr className="border-b-2 border-ink">
              <th className="text-left py-2 px-3 font-mono text-[10px] uppercase tracking-wider text-accent">Dial</th>
              <th className="text-left py-2 px-3 font-mono text-[10px] uppercase tracking-wider text-accent">Means</th>
              <th className="text-left py-2 px-3 font-mono text-[10px] uppercase tracking-wider text-accent">You pick</th>
              <th className="text-left py-2 px-3 font-mono text-[10px] uppercase tracking-wider text-accent">Camera picks</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['Auto / 🟩', '"Just take a photo"', 'nothing', 'everything'],
              ['P', '"Mostly automatic, but I can adjust"', 'ISO', 'aperture + shutter'],
              ['Av', '"I care about the background"', 'aperture, ISO', 'shutter'],
              ['Tv', '"Something is moving"', 'shutter, ISO', 'aperture'],
              ['M', '"I know what I\'m doing"', 'everything', 'nothing'],
            ].map(([dial, means, you, cam], i) => (
              <tr key={i} className="border-b border-rule">
                <td className="py-2 px-3 font-bold">{dial}</td>
                <td className="py-2 px-3">{means}</td>
                <td className="py-2 px-3">{you}</td>
                <td className="py-2 px-3">{cam}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <Definition label="Beginner default">
          <strong>Av</strong> is the right starting place for almost everything. You pick the look (blurry vs sharp background); the camera handles the rest. Use <strong>Tv</strong> only when motion-freezing matters (sport, kids, dancing).
        </Definition>
      </Section>

      <Section id="ph-focus" num="05" title="How to focus">
        <p>The shutter button has two stages:</p>
        <ol className="list-decimal pl-6 my-4 space-y-2">
          <li><strong>Half-press</strong>: camera focuses.</li>
          <li><strong>Full press</strong>: takes the photo.</li>
        </ol>
        <p>Two simple ways to tell the camera <em>where</em> to focus: the viewfinder (with arrow buttons to move the focus point) or the back screen — <strong>tap on what you want sharp</strong>.</p>
        <Note label="For people">
          Always focus on the <strong>nearest eye</strong>.
        </Note>
      </Section>

      <Section id="ph-recipes" num="06" title="Settings for five common photos">
        <p>Five simple cards. Set the dial, set the f-number, push the shutter. ISO on Auto throughout.</p>

        <CardGrid>
          {[
            ['📷 Portrait', 'Av', 'f/2.8', 'Auto', 'Tap the near eye on the screen'],
            ['🏔️ Landscape', 'Av', 'f/8', '100', 'About a third into the scene'],
            ['🏠 Indoor', 'Av', 'f/2.8', 'Auto (cap 6400)', 'Tap-to-focus on the screen'],
            ['🏃 Sport', 'Tv', '—', 'Auto', 'AI Servo, continuous drive'],
            ['🌆 Travel', 'Av', 'f/5.6', 'Auto', 'A 24–105 mm zoom is perfect'],
          ].map(([heading, mode, ap, iso, focus]) => (
            <Card key={heading} heading={heading}>
              <p><strong>Mode:</strong> <code>{mode}</code></p>
              <p><strong>Aperture:</strong> <code>{ap}</code></p>
              <p><strong>ISO:</strong> {iso}</p>
              <p><strong>Focus:</strong> {focus}</p>
            </Card>
          ))}
        </CardGrid>
      </Section>

      <Section id="ph-habits" num="07" title="Five small habits that improve every photo">
        <ol className="list-decimal pl-6 my-4 space-y-2">
          <li><strong>Move closer.</strong> Walk in. Or zoom in.</li>
          <li><strong>Get down (or up).</strong> Don't shoot everything from standing eye level.</li>
          <li><strong>Watch the light.</strong> Soft side-light from a window or the hour around sunrise / sunset.</li>
          <li><strong>Mind the background.</strong> Move two steps left to remove the lamppost behind your friend's head.</li>
          <li><strong>Take more than one.</strong> Eyes blink. Take three or four — keep the best.</li>
        </ol>
      </Section>

      <Section id="ph-try" num="08" title="Try these this week">
        <p>One small mission a day. They build on each other; do them in order.</p>

        <Exercise label="Mission 1 · A friend's portrait" difficulty="★">
          <p className="prompt"><code>Av</code>, <code>f/2.8</code>, ISO Auto. Step back, zoom in. Tap their nearest eye on the screen. Take three photos. Pick your favourite.</p>
          <Solution summary="What you should see">
            A sharp face with a softly blurry background — the unmistakable look of a "good camera." Step further back if you can; faces look better with a longer focal length.
          </Solution>
        </Exercise>

        <Exercise label="Mission 2 · A landscape at golden hour" difficulty="★">
          <p className="prompt"><code>Av</code>, <code>f/8</code>, ISO 100. Find a view 20 minutes before sunset. Take five photos as the light changes.</p>
          <Solution summary="What you should see">
            Warm light wrapping the scene; long shadows; richer colours than midday. Sunset light is the cheapest filter on earth.
          </Solution>
        </Exercise>

        <Exercise label="Mission 3 · An indoor photo without flash" difficulty="★">
          <p className="prompt"><code>Av</code>, <code>f/2.8</code>, ISO Auto. Find a window. Place your subject just to the side of the window so the light falls across them. No flash.</p>
          <Solution summary="What you should see">
            Soft, directional light with gentle shadow on the unlit side. Window light is what professional portrait photographers fight to recreate in studios.
          </Solution>
        </Exercise>
      </Section>
    </Layout>
  );
}
